import { useState, useEffect } from 'react';
import type { FormEvent } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { translations, type Language } from '@/lib/translations';

const getInitialLang = (): Language => {
  try {
    const stored = localStorage.getItem('guestLanguage');
    if (stored === 'en' || stored === 'fr') return stored;
  } catch { /* ignore */ }
  return 'en';
};

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

const Spinner = () => (
  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
  </svg>
);

const AUTH_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=DM+Serif+Display:ital@0;1&display=swap');
.auth-page-root{position:fixed;inset:0;overflow-y:auto;background:#080b14;color:#e8eaf6;font-family:'Sora',sans-serif;-webkit-font-smoothing:antialiased;z-index:9999;}
.auth-orb{position:absolute;border-radius:50%;filter:blur(80px);opacity:.45;animation:auth-drift 14s ease-in-out infinite;}
.auth-orb-1{width:480px;height:480px;background:radial-gradient(circle,#5b4fcf 0%,transparent 70%);top:-160px;right:-120px;animation-delay:0s;}
.auth-orb-2{width:360px;height:360px;background:radial-gradient(circle,#0e5f8c 0%,transparent 70%);bottom:-100px;left:-80px;animation-delay:-5s;}
.auth-orb-3{width:260px;height:260px;background:radial-gradient(circle,#7c3aed 0%,transparent 70%);top:40%;left:30%;animation-delay:-9s;}
@keyframes auth-drift{0%,100%{transform:translate(0,0) scale(1);}33%{transform:translate(30px,-40px) scale(1.07);}66%{transform:translate(-20px,25px) scale(.95);}}
.auth-bg-grid{position:fixed;inset:0;z-index:0;pointer-events:none;background-image:linear-gradient(rgba(255,255,255,.025) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.025) 1px,transparent 1px);background-size:48px 48px;}
.auth-card{position:relative;width:100%;max-width:440px;background:rgba(14,18,32,.78);border:1px solid rgba(255,255,255,.13);border-radius:24px;backdrop-filter:blur(24px);-webkit-backdrop-filter:blur(24px);padding:2.5rem 2.25rem 2rem;box-shadow:0 0 0 1px rgba(108,99,255,.1),0 40px 80px rgba(0,0,0,.5),inset 0 1px 0 rgba(255,255,255,.06);animation:auth-rise .55s cubic-bezier(.16,1,.3,1) both;}
.auth-card::before{content:'';position:absolute;top:0;left:10%;right:10%;height:1px;background:linear-gradient(90deg,transparent,rgba(167,139,250,.6),rgba(56,189,248,.6),transparent);border-radius:99px;}
@keyframes auth-rise{from{opacity:0;transform:translateY(28px) scale(.97);}to{opacity:1;transform:translateY(0) scale(1);}}
.auth-brand-name{font-family:'DM Serif Display',serif;font-size:2.1rem;letter-spacing:-.02em;background:linear-gradient(135deg,#e0deff 20%,#a78bfa 60%,#38bdf8 100%);-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text;margin-bottom:.35rem;line-height:1.2;}
.auth-logo-bg{position:absolute;inset:0;border-radius:20px;background:linear-gradient(135deg,#6c63ff 0%,#a78bfa 50%,#38bdf8 100%);box-shadow:0 0 32px rgba(108,99,255,.5),0 8px 24px rgba(0,0,0,.4);animation:auth-pulse 3s ease-in-out infinite;}
@keyframes auth-pulse{0%,100%{box-shadow:0 0 32px rgba(108,99,255,.5),0 8px 24px rgba(0,0,0,.4);}50%{box-shadow:0 0 48px rgba(108,99,255,.75),0 8px 32px rgba(0,0,0,.4);}}
.auth-lang-btn{padding:4px 14px;border-radius:99px;font-size:11px;font-weight:600;letter-spacing:.07em;text-transform:uppercase;border:none;cursor:pointer;transition:all .2s;background:transparent;color:#7b82a8;}
.auth-lang-btn.active{background:linear-gradient(135deg,#6c63ff,#a78bfa);color:#fff;box-shadow:0 2px 8px rgba(108,99,255,.4);}
.auth-input{width:100%;background:#141829;border:1px solid rgba(255,255,255,.07);border-radius:10px;padding:13px 16px;font-size:14.5px;font-family:'Sora',sans-serif;color:#e8eaf6;outline:none;transition:border-color .2s,box-shadow .2s,background .2s;}
.auth-input::placeholder{color:#3d4468;}
.auth-input:focus{border-color:#6c63ff;background:rgba(108,99,255,.06);box-shadow:0 0 0 3px rgba(108,99,255,.15);}
.auth-btn-primary{width:100%;padding:14px;border:none;border-radius:10px;font-family:'Sora',sans-serif;font-size:15px;font-weight:600;cursor:pointer;position:relative;overflow:hidden;background:linear-gradient(135deg,#5b52e8 0%,#7c6ef0 50%,#5baae8 100%);color:#fff;box-shadow:0 4px 24px rgba(108,99,255,.4),inset 0 1px 0 rgba(255,255,255,.15);transition:transform .15s,box-shadow .15s;letter-spacing:.01em;display:flex;align-items:center;justify-content:center;gap:8px;}
.auth-btn-primary:hover{transform:translateY(-1px);box-shadow:0 6px 32px rgba(108,99,255,.55);}
.auth-btn-primary:active{transform:translateY(0);}
.auth-btn-primary:disabled{opacity:.65;cursor:not-allowed;transform:none;}
.auth-btn-google{width:100%;padding:13px;background:#141829;border:1px solid rgba(255,255,255,.13);border-radius:10px;font-family:'Sora',sans-serif;font-size:14px;font-weight:500;color:#e8eaf6;cursor:pointer;display:flex;align-items:center;justify-content:center;gap:10px;transition:background .2s,border-color .2s,transform .15s;}
.auth-btn-google:hover{background:rgba(255,255,255,.06);border-color:rgba(255,255,255,.2);transform:translateY(-1px);}
.auth-btn-google:active{transform:translateY(0);}
.auth-btn-google:disabled{opacity:.65;cursor:not-allowed;transform:none;}
.auth-label{display:block;font-size:12px;font-weight:600;letter-spacing:.05em;text-transform:uppercase;color:#7b82a8;margin-bottom:.5rem;}
.auth-error{background:rgba(248,113,113,.1);border-left:3px solid #f87171;border-radius:8px;padding:12px 14px;font-size:13px;color:#fca5a5;margin-bottom:1rem;}
.auth-divider-line{flex:1;height:1px;background:rgba(255,255,255,.07);}
.auth-divider-text{font-size:12px;color:#7b82a8;white-space:nowrap;}
.auth-footer-brand{background:linear-gradient(135deg,#6c63ff,#a78bfa);-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text;font-weight:600;}
.auth-accent-link{color:#a78bfa;font-weight:600;text-decoration:none;transition:color .2s;}
.auth-accent-link:hover{color:#38bdf8;}
.auth-ghost-btn{background:transparent;border:none;cursor:pointer;font-family:'Sora',sans-serif;font-size:13px;color:#7b82a8;transition:color .2s;padding:0;}
.auth-ghost-btn:hover{color:#e8eaf6;}
.auth-particle{position:absolute;border-radius:50%;opacity:0;animation:auth-float-up linear infinite;}
@keyframes auth-float-up{0%{opacity:0;transform:translateY(0) scale(1);}10%{opacity:.6;}90%{opacity:.2;}100%{opacity:0;transform:translateY(-100vh) scale(.3);}}
`;

export function Login() {
  const [lang, setLangState] = useState<Language>(getInitialLang);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetSent, setResetSent] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState('');

  const { login, loginWithGoogle } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const id = 'auth-page-styles';
    if (!document.getElementById(id)) {
      const style = document.createElement('style');
      style.id = id;
      style.textContent = AUTH_CSS;
      document.head.appendChild(style);
    }
  }, []);

  const t = (key: string): string => {
    const keys = key.split('.');
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let value: any = translations[lang];
    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) value = value[k];
      else return key;
    }
    return typeof value === 'string' ? value : key;
  };

  const handleLang = (l: Language) => {
    setLangState(l);
    try { localStorage.setItem('guestLanguage', l); } catch { /* ignore */ }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault(); setError(''); setLoading(true);
    try { await login(email, password); navigate('/dashboard'); }
    catch (err) { setError(err instanceof Error ? err.message : t('errors.loadError')); }
    finally { setLoading(false); }
  };

  const handleGoogle = async () => {
    setError(''); setLoading(true);
    try { await loginWithGoogle(); navigate('/dashboard'); }
    catch (err) { setError(err instanceof Error ? err.message : t('errors.loadError')); }
    finally { setLoading(false); }
  };

  const handleForgot = async (e: FormEvent) => {
    e.preventDefault(); setResetError(''); setResetLoading(true);
    try { await sendPasswordResetEmail(auth, resetEmail); setResetSent(true); }
    catch (err) { setResetError(err instanceof Error ? err.message : t('errors.loadError')); }
    finally { setResetLoading(false); }
  };

  const backToLogin = () => {
    setShowForgot(false); setResetEmail(''); setResetSent(false); setResetError('');
  };

  const PARTICLES = Array.from({ length: 28 }, (_, i) => {
    const colors = ['#6c63ff','#a78bfa','#38bdf8','#f4c55a'];
    const size = `${1.2 + (i * 0.11) % 2.3}px`;
    return (
      <div key={i} className="auth-particle" style={{
        left: `${(i * 37) % 100}vw`,
        bottom: '-10px',
        width: size, height: size,
        background: colors[i % 4],
        animationDuration: `${13 + (i * 0.7) % 19}s`,
        animationDelay: `${(i * 0.75) % 20}s`,
      }} />
    );
  });

  return (
    <div className="auth-page-root">
      {/* Orbs */}
      <div style={{ position:'fixed', inset:0, zIndex:0, overflow:'hidden', pointerEvents:'none' }}>
        <div className="auth-orb auth-orb-1" />
        <div className="auth-orb auth-orb-2" />
        <div className="auth-orb auth-orb-3" />
      </div>
      <div className="auth-bg-grid" />
      <div style={{ position:'fixed', inset:0, zIndex:0, pointerEvents:'none' }}>{PARTICLES}</div>

      {/* Center */}
      <div style={{ position:'relative', zIndex:1, minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', padding:'2rem 1rem' }}>
        <div className="auth-card">

          {/* Language */}
          <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:'1.5rem' }}>
            <div style={{ display:'flex', gap:'2px', background:'#141829', border:'1px solid rgba(255,255,255,.07)', borderRadius:'99px', padding:'3px' }}>
              {(['en','fr'] as Language[]).map(l => (
                <button key={l} type="button" className={`auth-lang-btn${lang === l ? ' active' : ''}`} onClick={() => handleLang(l)}>{l.toUpperCase()}</button>
              ))}
            </div>
          </div>

          {/* Logo */}
          <div style={{ textAlign:'center', marginBottom:'2rem' }}>
            <div style={{ width:68, height:68, margin:'0 auto 1rem', position:'relative', display:'flex', alignItems:'center', justifyContent:'center' }}>
              <div className="auth-logo-bg" />
              <span style={{ fontSize:32, position:'relative', zIndex:1 }}>💰</span>
            </div>
            <div className="auth-brand-name">MyBalance</div>
            <div style={{ fontSize:'13.5px', color:'#7b82a8' }}>
              {showForgot
                ? t('auth.resetPassword')
                : lang === 'en' ? 'Welcome back, sign in to continue' : 'Content de vous revoir, connectez-vous'}
            </div>
          </div>

          {/* Forgot password panel */}
          {showForgot ? (
            <>
              {resetSent ? (
                <div style={{ textAlign:'center' }}>
                  <div style={{ margin:'0 auto 1rem', width:56, height:56, borderRadius:'50%', background:'rgba(52,211,153,.15)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                    <svg width="28" height="28" fill="none" viewBox="0 0 24 24" stroke="#34d399" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                  </div>
                  <div style={{ fontSize:'16px', fontWeight:600, color:'#e8eaf6', marginBottom:'.5rem' }}>{t('auth.checkYourEmail')}</div>
                  <div style={{ fontSize:'13px', color:'#7b82a8', marginBottom:'1.5rem' }}>
                    {t('auth.resetEmailSent')} <strong style={{ color:'#e8eaf6' }}>{resetEmail}</strong>
                  </div>
                  <button className="auth-ghost-btn" onClick={backToLogin}>&larr; {t('auth.backToSignIn')}</button>
                </div>
              ) : (
                <>
                  {resetError && <div className="auth-error">{resetError}</div>}
                  <p style={{ fontSize:'13px', color:'#7b82a8', marginBottom:'1.25rem' }}>{t('auth.resetPasswordDescription')}</p>
                  <form onSubmit={handleForgot}>
                    <div style={{ marginBottom:'1rem' }}>
                      <label className="auth-label">{t('auth.email')}</label>
                      <input className="auth-input" type="email" placeholder="you@example.com" value={resetEmail} onChange={e => setResetEmail(e.target.value)} required disabled={resetLoading} />
                    </div>
                    <button type="submit" className="auth-btn-primary" disabled={resetLoading}>
                      {resetLoading ? <><Spinner />{t('auth.sendingReset')}</> : t('auth.sendResetEmail')}
                    </button>
                  </form>
                  <div style={{ textAlign:'center', marginTop:'1rem' }}>
                    <button className="auth-ghost-btn" onClick={backToLogin}>&larr; {t('auth.backToSignIn')}</button>
                  </div>
                </>
              )}
            </>
          ) : (
            /* Login panel */
            <>
              {error && <div className="auth-error">{error}</div>}
              <form onSubmit={handleSubmit}>
                <div style={{ marginBottom:'1rem' }}>
                  <label className="auth-label">{t('auth.email')}</label>
                  <input className="auth-input" type="email" placeholder="you@example.com" value={email} onChange={e => setEmail(e.target.value)} required disabled={loading} autoComplete="email" />
                </div>
                <div style={{ marginBottom:'1rem' }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'.5rem' }}>
                    <label className="auth-label" style={{ marginBottom:0 }}>{t('auth.password')}</label>
                    <button type="button" className="auth-accent-link" style={{ fontSize:'12px', background:'none', border:'none', cursor:'pointer', padding:0 }}
                      onClick={() => { setShowForgot(true); setResetEmail(email); }}>
                      {t('auth.forgotPassword')}
                    </button>
                  </div>
                  <input className="auth-input" type="password" placeholder="••••••••" value={password} onChange={e => setPassword(e.target.value)} required disabled={loading} autoComplete="current-password" />
                </div>
                <button type="submit" className="auth-btn-primary" disabled={loading} style={{ marginTop:'1.25rem' }}>
                  {loading ? <><Spinner />{t('auth.signingIn')}</> : <>{t('auth.signIn')} &rarr;</>}
                </button>
              </form>

              <div className="hidden sm:block">
                <div style={{ display:'flex', alignItems:'center', gap:'12px', margin:'1.5rem 0' }}>
                  <div className="auth-divider-line" />
                  <span className="auth-divider-text">{t('auth.continueWith')}</span>
                  <div className="auth-divider-line" />
                </div>
                <button className="auth-btn-google" onClick={handleGoogle} disabled={loading}>
                  <GoogleIcon />
                  <span>{lang === 'en' ? 'Continue with Google' : 'Continuer avec Google'}</span>
                </button>
              </div>

              <div style={{ textAlign:'center', fontSize:'13px', color:'#7b82a8', marginTop:'1.5rem' }}>
                <span>{t('auth.dontHaveAccount')} </span>
                <Link to="/signup" className="auth-accent-link">{t('auth.createAccount')} &rarr;</Link>
              </div>
            </>
          )}

          {/* Footer */}
          <div style={{ marginTop:'1.75rem', paddingTop:'1.25rem', borderTop:'1px solid rgba(255,255,255,.07)', textAlign:'center', fontSize:'12px', color:'#3d4468' }}>
            MyBalance by <span className="auth-footer-brand">Unova</span>
          </div>
        </div>
      </div>
    </div>
  );
}
